import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Typography,
  Link,
  Button,
  Divider,
  Alert,
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import UserRegistrationForm from '@components/auth/UserRegistrationForm';
import OrgAdminRegistrationForm from '@components/auth/OrgAdminRegistrationForm';
import { useAuth } from '@hooks/useAuth';

const RegisterPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signinWithGoogle } = useAuth();

  const inviteCode = searchParams.get('invite') || '';

  // Check if redirected from Google Sign-In
  const [googleUser, setGoogleUser] = useState(location.state?.googleUser || null);

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle Google Sign-In (for users who haven't signed in yet)
  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      const result = await signinWithGoogle();

      // If existing user, redirect to dashboard
      if (!result.isNewUser) {
        navigate('/dashboard', { replace: true });
      } else {
        // If new user, set googleUser state so forms can use it
        setGoogleUser(result.user);
      }
    } catch (err) {
      setError(err.message || 'Failed to sign in with Google.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 2,
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Create Account
          </Typography>

          {/* Show success message if Google user */}
          {googleUser && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="medium">
                Successfully authenticated with Google!
              </Typography>
              <Typography variant="body2">
                Please complete your registration below by entering your organization's invite code.
              </Typography>
            </Alert>
          )}

          {/* Show Google Sign-In option if not already a Google user */}
          {!googleUser && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<GoogleIcon />}
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                >
                  Sign up with Google
                </Button>
              </Box>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>
            </>
          )}

          {/* Registration Type Tabs */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ mb: 3 }}
          >
            <Tab label="Volunteer" />
            <Tab label="Organization Admin" />
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ mt: 3 }}>
            {tabValue === 0 && <UserRegistrationForm googleUser={googleUser} defaultInviteCode={inviteCode} />}
            {tabValue === 1 && <OrgAdminRegistrationForm googleUser={googleUser} />}
          </Box>

          {/* Sign In Link */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link
                component={RouterLink}
                to="/login"
                underline="hover"
                fontWeight="medium"
              >
                Sign in
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default RegisterPage;
